// src/oura/components/aeo/CopyTextButton.tsx
//
// Put the proposed wording on the clipboard, verbatim (#3575).
//
// On a page Oura cannot publish to — an Elementor body, a shortcode-rendered
// section — applying the change by hand is the only route, and on the client
// this was measured against it is 17 of 20 pages. The reviewer's only option
// was to select the new wording out of a diff view by hand, which is fiddly
// precisely because the diff puts the old and new text next to each other.
//
// Two things this must not get wrong:
//
//   * **Verbatim.** The string written is the same string a publish would have
//     written — no diff markers, no surrounding markup, no truncation. If the
//     clipboard and the publish path can disagree, the copy button is a
//     different feature that merely looks like this one.
//   * **Say it worked.** A clipboard write with no feedback is indistinguishable
//     from a dead button, and the reviewer finds out by pasting nothing into
//     WordPress.

import { useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { cn } from "../../lib/cn";

type State = "idle" | "copied" | "failed";

export function CopyTextButton({
    text,
    className,
    label = "Copy text",
}: {
    /** Written to the clipboard exactly as given. */
    text: string;
    className?: string;
    label?: string;
}) {
    const [state, setState] = useState<State>("idle");
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const copy = async () => {
        window.clearTimeout(timer.current);
        try {
            await navigator.clipboard.writeText(text);
            setState("copied");
        } catch {
            // Blocked permission, or an insecure context where the API does not
            // exist. Said out loud rather than swallowed: the whole point of the
            // confirmation is that the reviewer knows before they walk over to
            // WordPress with an empty clipboard.
            setState("failed");
        }
        timer.current = window.setTimeout(() => setState("idle"), 2000);
    };

    return (
        <button
            type="button"
            onClick={() => void copy()}
            aria-live="polite"
            className={cn(
                "inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium transition-colors",
                state === "copied" && "text-emerald-600",
                state === "failed" && "text-rose-600",
                state === "idle" && "text-gray-500 hover:text-gray-800",
                className,
            )}
        >
            {state === "copied" ? (
                <CheckIcon className="h-3 w-3" />
            ) : (
                <CopyIcon className="h-3 w-3" />
            )}
            {state === "copied" ? "Copied" : state === "failed" ? "Could not copy" : label}
        </button>
    );
}
